// ===========================================
// Calendar Event Context Menu
// ===========================================
// Right-click menu for calendar events with
// "Cancel this class" option

import { useEffect } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CalendarEvent } from '../../../api/hybridBooking.api';
import { DRAGGABLE_TYPES } from './calendar.constants';

interface CalendarEventContextMenuProps {
  anchorPosition: { top: number; left: number } | null;
  event: CalendarEvent | null;
  basePath: string;
  onClose: () => void;
  onCancelOccurrence: (event: CalendarEvent) => void;
  onViewDetails: (event: CalendarEvent) => void;
}

export default function CalendarEventContextMenu({
  anchorPosition,
  event,
  basePath,
  onClose,
  onCancelOccurrence,
  onViewDetails,
}: CalendarEventContextMenuProps) {
  // Close menu on scroll or window resize
  useEffect(() => {
    if (!anchorPosition) return;
    const handleDismiss = () => onClose();
    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    return () => {
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [anchorPosition, onClose]);

  if (!event) return null;

  const isDraggable = event.resource?.type
    ? DRAGGABLE_TYPES.includes(event.resource.type)
    : false;
  // An already-cancelled occurrence can't be cancelled again — the backend 409s
  // ("An exception already exists for this occurrence"), so offering the action
  // is a dead end. Cancelled events DO render (red outline + strikethrough), so
  // this menu is reachable on them.
  const isCancelled = event.resource?.isCancelled === true;

  return (
    <Menu
      open={!!anchorPosition}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
    >
      <MenuItem
        onClick={() => {
          onClose();
          onViewDetails(event);
        }}
      >
        <ListItemIcon>
          <InfoIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>View details</ListItemText>
      </MenuItem>
      {event.resource?.lessonId && (
        <MenuItem
          component="a"
          href={`${basePath}/classes/${event.resource.lessonId}`}
          target="_blank"
          onClick={onClose}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>
      )}
      {isDraggable && !isCancelled && (
        <>
          <Divider />
          <MenuItem
            onClick={() => {
              onCancelOccurrence(event);
              onClose();
            }}
          >
            <ListItemIcon>
              <CancelIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Cancel this class</ListItemText>
          </MenuItem>
        </>
      )}
      {isDraggable && isCancelled && (
        <>
          <Divider />
          <MenuItem disabled>
            <ListItemIcon>
              <EventBusyIcon fontSize="small" />
            </ListItemIcon>
            {/* No "Restore" action offered yet — the endpoint and hook exist
                (DELETE /lessons/:id/exceptions/:exceptionId,
                useDeleteLessonException) — restore now has a UI on the Class Details card (2026-09-06); the rest tracked as P4.2-P4.5.
                Don't promise a restore path here until that ships. */}
            <ListItemText primary="Already cancelled" />
          </MenuItem>
        </>
      )}
    </Menu>
  );
}
